import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormTemplate, FormTab, FormField, FormAnswer, FormResponse, ResponseStatus, FormResponseRequest, FileRef } from '../models/form.model';
import { FormValidationService } from '../service/form-validation.service';
import { FormTextFieldComponent } from '../fields/text-field.component';
import { FormSelectOneFieldComponent } from '../fields/select-one-field.component';
import { FormSelectMultipleFieldComponent } from '../fields/select-multiple-field.component';
import { FormFileUploadFieldComponent } from '../fields/file-upload-field.component';
import { FormPictureUploadFieldComponent } from '../fields/picture-upload-field.component';
import { FormDescriptionFieldComponent } from '../fields/description-field.component';
import { FormImageDisplayFieldComponent } from '../fields/image-display-field.component';
import { FormResponseService } from '../service/form-response.service';
import { FormService } from '../service/form.service';
import { ProjecttaskService } from '../../pages/portal/projecttask/service/projecttask.service';
import { Task } from '@src/app/models/task';
import { Subject, Subscription, timer } from 'rxjs';
import { switchMap, mergeMap } from 'rxjs/operators';
import { SessionHelper } from '@src/app/helpers/sessionHelper';

@Component({
  selector: 'app-form-renderer',
  standalone: true,
  imports: [
    CommonModule,
    FormTextFieldComponent,
    FormSelectOneFieldComponent,
    FormSelectMultipleFieldComponent,
    FormFileUploadFieldComponent,
    FormPictureUploadFieldComponent,
    FormDescriptionFieldComponent,
    FormImageDisplayFieldComponent
  ],
  templateUrl: './form-renderer.component.html',
  styleUrls: ['./form-renderer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormRendererComponent implements OnInit {
  @Input() template!: FormTemplate;
  @Input() existingResponse?: FormResponse;
  @Input() organizationId!: string;
  @Input() projectId?: string;
  @Input() taskId?: string;
  @Input() task?: Task; // Full task object for completion
  @Input() formTemplateId?: string; // Alternative to template input
  @Input() formTemplateVersion?: number; // Alternative to template input
  @Input() customerId?: string; // Customer ID for task context
  @Input() context?: string; // Context for form display ('form-builder', 'form-list', 'customerportal-task', 'task-detail')
  @Output() submitted = new EventEmitter<FormResponse>();
  @Output() draftSaved = new EventEmitter<FormResponse>();
  @Output() taskCompleted = new EventEmitter<string>(); // Emits new taskId version
  @Output() formSubmitted = new EventEmitter<FormResponse>(); // For task modal integration
  @Output() formCancelled = new EventEmitter<void>(); // For task modal integration

  currentTabIndex = 0;
  currentTab: FormTab | undefined;
  answers: FormAnswer[] = [];
  fieldErrors: Map<string, any> = new Map();
  tabErrors: Map<number, number> = new Map(); // tabIndex -> error count
  progressPercent = 0;

  // Autosave state
  private changes$ = new Subject<void>();
  private autosaveSub?: Subscription;
  savingDraft = false;
  submitting = false;
  lastSavedAt?: string;
  submitError?: string;
  showResetConfirmModal = false;
  resetting = false;
  resetError?: string;

  constructor(
    private validationService: FormValidationService,
    private responseService: FormResponseService,
    private formService: FormService,
    private taskService: ProjecttaskService,
    private changeDetectorRef: ChangeDetectorRef,
    @Optional() private dialogRef?: MatDialogRef<FormRendererComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: any
  ) {
    // Handle dialog data initialization
    if (this.dialogData) {
      this.template = this.dialogData.template;
      this.existingResponse = this.dialogData.existingResponse;
      this.organizationId = this.dialogData.organizationId;
      this.projectId = this.dialogData.projectId;
      this.taskId = this.dialogData.taskId;
      this.task = this.dialogData.task;
      this.customerId = this.dialogData.customerId;
      this.context = this.dialogData.context;
    }
  }

  closeModal(): void {
    this.formCancelled.emit();
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  /**
   * Determines if the form is in view/read-only mode (viewing a submitted form)
   * View mode is active when existingResponse exists and has status 'submitted'
   */
  get isViewMode(): boolean {
    return this.existingResponse?.status === 'submitted';
  }

  /**
   * Determines if the current user is a vendor/admin user (not a customer user)
   * Vendor/admin users can perform administrative actions like resetting forms to draft
   */
  get isVendorUser(): boolean {
    return !SessionHelper.getCustomer();
  }

  ngOnInit(): void {
    // If formTemplateId is provided but not template, load it
    if (this.formTemplateId && !this.template) {
      
      // Parse template ID - it might be in format "templateId_vN"
      let templateId = this.formTemplateId;
      let templateVersion = this.formTemplateVersion;
      
      if (this.formTemplateId.includes('_v')) {
        const parts = this.formTemplateId.split('_v');
        templateId = parts[0];
        if (!templateVersion && parts[1]) {
          templateVersion = parseInt(parts[1], 10);
        }
        console.log('[FormRenderer] Parsed template ID:', { templateId, templateVersion });
      }
      
      this.formService.getTemplate(this.organizationId, templateId).subscribe({
        next: (templates) => {
          // Find the template with the specified version, or use the first one
          let selectedTemplate = templates.find(t => t.version === templateVersion);
          if (!selectedTemplate && templates.length > 0) {
            selectedTemplate = templates[0];
            console.log('[FormRenderer] Version not found, using first template:', selectedTemplate);
          }
          
          if (selectedTemplate) {
            this.template = selectedTemplate;
            this.initializeTemplate();
            this.changeDetectorRef.markForCheck();
          } else {
            console.error('[FormRenderer] No template found for ID:', templateId);
          }
        },
        error: (err) => {
          console.error('[FormRenderer] Failed to load template:', err);
        }
      });
      return;
    }
    
    if (!this.template) {
      console.error('FormRenderer: no template provided');
      return;
    }

    this.initializeTemplate();
  }

  private initializeTemplate(): void {

    // Normalize template id and tabs
    this.template = {
      ...this.template,
      id: (this.template as any).id || (this.template as any).templateId,
      tabs: (() => {
        const rawTabs = (this.template as any).tabs;
        if (Array.isArray(rawTabs)) return rawTabs;
        if (typeof rawTabs === 'string') {
          try {
            return JSON.parse(rawTabs);
          } catch (err) {
            console.error('Failed to parse template tabs', err);
            return [];
          }
        }
        return [];
      })()
    };

    this.template.tabs = this.template.tabs || [];

    if (this.template.tabs.length > 0) {
      this.selectTab(0);
      if (this.existingResponse) {
        console.log('[FormRenderer] Existing response loaded:', this.existingResponse);
        this.answers = [...this.existingResponse.answers];
        console.log('[FormRenderer] Loaded answers from existing response:', this.answers);
        
        // Load file references from existingResponse.files back into answers
        // Map each file to the corresponding file field by position/order
        const fileFields = this.template.tabs
          .flatMap(t => t.fields)
          .filter(f => f.type === 'file');
        
        console.log('[FormRenderer] File fields in template:', fileFields.map(f => ({ id: f.id, label: f.label })));
        console.log('[FormRenderer] Files in response:', this.existingResponse.files);
        
        if (this.existingResponse.files && fileFields.length > 0) {
          this.existingResponse.files.forEach((fileRef, index) => {
            console.log(`[FormRenderer] Processing file ${index}:`, fileRef);
            if (index < fileFields.length) {
              const fieldId = fileFields[index].id;
              // Add or update the answer for this file field
              const existingAnswerIndex = this.answers.findIndex(a => a.fieldId === fieldId);
              if (existingAnswerIndex >= 0) {
                this.answers[existingAnswerIndex].value = fileRef;
              } else {
                this.answers.push({ fieldId, value: fileRef });
              }
            }
          });
        }

        // Load image references from existingResponse.images back into answers
        // Since answers should already contain image data if it was saved properly,
        // we only need to restore from existingResponse.images if they're provided separately
        const imageFields = this.template.tabs
          .flatMap(t => t.fields)
          .filter(f => f.type === 'image');
        
        console.log('[FormRenderer] Image fields in template:', imageFields.map(f => ({ id: f.id, label: f.label })));
        console.log('[FormRenderer] Images in response:', this.existingResponse.images);
        console.log('[FormRenderer] Full image object:', JSON.stringify(this.existingResponse.images, null, 2));
        console.log('[FormRenderer] Current answers after file load:', this.answers);
        
        // Check if the answers already have complete image data
        const imageAnswers = this.answers.filter(a => 
          imageFields.some(f => f.id === a.fieldId)
        );
        console.log('[FormRenderer] Image answers from server:', imageAnswers);
        
        // First check if image data in answers is complete (has URL)
        const answersHaveCompleteImages = imageAnswers.every(a => 
          a.value && typeof a.value === 'object' && 'url' in a.value && a.value.url
        );
        
        console.log('[FormRenderer] Answers have complete image data:', answersHaveCompleteImages);
        
        // If answers don't have complete images but we have complete images in the response.images array,
        // and those images have URLs, use them
        if (!answersHaveCompleteImages && this.existingResponse.images && imageFields.length > 0) {
          // Try to enrich incomplete image data with complete data
          const completeImages = this.existingResponse.images.filter(img => img && img.url);
          if (completeImages.length > 0) {
            completeImages.forEach((imageRef, index) => {
              console.log(`[FormRenderer] Using complete image ${index}:`, imageRef);
              if (index < imageFields.length) {
                const fieldId = imageFields[index].id;
                const existingAnswerIndex = this.answers.findIndex(a => a.fieldId === fieldId);
                if (existingAnswerIndex >= 0) {
                  this.answers[existingAnswerIndex].value = imageRef;
                } else {
                  this.answers.push({ fieldId, value: imageRef });
                }
              }
            });
          }
        }

      }
      this.calculateProgress();
      this.validateAllTabs();
    }

    // Debounced autosave: fire after 3s of inactivity
    this.autosaveSub = this.changes$
      .pipe(switchMap(() => timer(3000)))
      .subscribe(() => this.onSaveDraft(true));
  }

  selectTab(index: number): void {
    if (index >= 0 && index < this.template.tabs.length) {
      this.currentTabIndex = index;
      this.currentTab = this.template.tabs[index];
      this.validateCurrentTab();
    }
  }

  previousTab(): void {
    this.selectTab(this.currentTabIndex - 1);
  }

  nextTab(): void {
    this.selectTab(this.currentTabIndex + 1);
  }

  getAnswerValue(fieldId: string): any {
    const answer = this.answers.find(a => a.fieldId === fieldId);
    return answer?.value;
  }

  setAnswer(fieldId: string, value: any): void {
    const idx = this.answers.findIndex(a => a.fieldId === fieldId);
    if (idx >= 0) {
      this.answers[idx] = { fieldId, value };
    } else {
      this.answers.push({ fieldId, value });
    }
    // Mark component for change detection
    this.changeDetectorRef.markForCheck();
    // Schedule autosave on change
    this.changes$.next();
    // Recalculate progress
    this.calculateProgress();
    this.validateCurrentTab();
  }

  calculateProgress(): void {
    const allFields = this.template.tabs.flatMap(t => t.fields)
      .filter(f => f.type !== 'description' && f.type !== 'image-display'); // Exclude display-only fields
    const requiredFields = allFields.filter(f => f.required);
    
    if (requiredFields.length === 0) {
      // If no required fields, calculate based on all fields answered
      const answeredAll = allFields.filter(f => {
        const answer = this.answers.find(a => a.fieldId === f.id);
        if (!answer || answer.value === undefined || answer.value === null || answer.value === '') return false;
        if (Array.isArray(answer.value) && answer.value.length === 0) return false;
        return true;
      });
      this.progressPercent = allFields.length > 0 ? Math.round((answeredAll.length / allFields.length) * 100) : 0;
      return;
    }
    
    const answeredRequired = requiredFields.filter(f => {
      const answer = this.answers.find(a => a.fieldId === f.id);
      if (!answer || answer.value === undefined || answer.value === null || answer.value === '') return false;
      if (Array.isArray(answer.value) && answer.value.length === 0) return false;
      return true;
    });
    
    this.progressPercent = Math.round((answeredRequired.length / requiredFields.length) * 100);
  }

  validateCurrentTab(): void {
    if (!this.currentTab) return;
    
    let errorCount = 0;
    this.currentTab.fields.forEach(field => {
      const answer = this.answers.find(a => a.fieldId === field.id);
      const error = this.validationService.validateField(field, answer?.value);
      if (error) {
        errorCount++;
      }
    });
    
    this.tabErrors.set(this.currentTabIndex, errorCount);
  }

  validateAllTabs(): void {
    this.template.tabs.forEach((tab, index) => {
      let errorCount = 0;
      tab.fields.forEach(field => {
        const answer = this.answers.find(a => a.fieldId === field.id);
        const error = this.validationService.validateField(field, answer?.value);
        if (error) errorCount++;
      });
      this.tabErrors.set(index, errorCount);
    });
  }

  getTabErrorCount(tabIndex: number): number {
    return this.tabErrors.get(tabIndex) || 0;
  }

  hasTabErrors(tabIndex: number): boolean {
    return this.getTabErrorCount(tabIndex) > 0;
  }

  setFieldError(fieldId: string, error: any): void {
    if (error) {
      this.fieldErrors.set(fieldId, error);
    } else {
      this.fieldErrors.delete(fieldId);
    }
  }

  hasErrors(): boolean {
    return this.fieldErrors.size > 0;
  }

  private collectFileRefs(): FileRef[] {
    if (!this.template || !this.template.tabs) return [];
    const fileFieldIds = new Set(
      this.template.tabs.flatMap(t => t.fields).filter(f => f.type === 'file').map(f => f.id)
    );
    return this.answers
      .filter(a => fileFieldIds.has(a.fieldId) && !!a.value)
      .map(a => a.value as FileRef);
  }

  private collectImageRefs(): any[] {
    if (!this.template || !this.template.tabs) return [];
    const imageFieldIds = new Set(
      this.template.tabs.flatMap(t => t.fields).filter(f => f.type === 'image').map(f => f.id)
    );
    const collected = this.answers
      .filter(a => imageFieldIds.has(a.fieldId) && !!a.value)
      .map(a => a.value);
    return collected;
  }

  private getNonDisplayAnswers(): FormAnswer[] {
    if (!this.template || !this.template.tabs) return this.answers;

    const fieldById = new Map<string, FormField>();
    this.template.tabs.forEach(tab => {
      tab.fields.forEach(field => {
        fieldById.set(field.id, field);
      });
    });

    return this.answers.filter(answer => {
      const field = fieldById.get(answer.fieldId);
      if (!field) {
        return false;
      }
      return field.type !== 'description' && field.type !== 'image-display';
    });
  }

  onImageSelected(fieldId: string, file: File): void {
    // Handle image upload; emit event for parent to handle signed URL
  }

  onSaveDraft(auto = false): void {
    if (!this.organizationId) return;
    const answers = this.getNonDisplayAnswers();
    const files = this.collectFileRefs();
    const images = this.collectImageRefs();
    const req: FormResponseRequest = {
      status: 'draft',
      answers,
      files,
      images,
      projectId: this.projectId
    };
    this.savingDraft = true;
    const done = (status: ResponseStatus) => {
      this.savingDraft = false;
      this.lastSavedAt = new Date().toISOString();
      this.changeDetectorRef.markForCheck();
      
      // Clear the saved message after 3 seconds
      if (auto) {
        setTimeout(() => {
          this.lastSavedAt = undefined;
          this.changeDetectorRef.markForCheck();
        }, 3000);
      }
      
      const response: FormResponse = {
        organizationId: this.organizationId,
        projectId: this.projectId || '',
        taskId: this.taskId,
        templateId: this.template.id,
        templateVersion: this.template.version,
        status: status,
        answers,
        files,
        images,
        updatedAt: this.lastSavedAt
      };
      // Emit only for manual saves or successful autosaves
      if (!auto) this.draftSaved.emit(response);
    };

    if (this.taskId) {
      this.responseService
        .saveResponse(this.organizationId, this.taskId, this.template.id, req)
        .subscribe(done);
    } else if (this.projectId) {
      this.responseService
        .saveProjectResponse(this.organizationId, this.projectId, this.template.id, req)
        .subscribe(done);
    }
  }

  onSubmit(): void {
    // Validate all fields in all tabs
    const allFields = this.template.tabs.flatMap(t => t.fields);
    const errors = this.validationService.validateFormResponse(allFields, this.answers);
    if (errors) {
      console.error('Form validation errors:', errors);
      this.submitError = 'Please correct validation errors before submitting.';
      return;
    }
    if (!this.organizationId) return;
    
    this.submitting = true;
    this.submitError = undefined;
    const answers = this.getNonDisplayAnswers();
    const files = this.collectFileRefs();
    const images = this.collectImageRefs();
    
    const req: FormResponseRequest = {
      status: 'submitted',
      answers,
      files,
      images,
      projectId: this.projectId
    };
    
    const emitSubmitted = () => {
      const now = new Date().toISOString();
      const response: FormResponse = {
        organizationId: this.organizationId,
        projectId: this.projectId || '',
        taskId: this.taskId,
        templateId: this.template.id,
        templateVersion: this.template.version,
        status: 'submitted',
        answers,
        files,
        images: [],
        submittedAt: now,
        updatedAt: now
      };
      this.submitted.emit(response);
      this.formSubmitted.emit(response);
    };

    if (this.taskId && this.task) {
      // Task-linked form: submit response then mark task complete
      this.responseService
        .saveResponse(this.organizationId, this.taskId, this.template.id, req)
        .pipe(
          mergeMap(() => {
            // Mark task as done
            const taskToComplete = { ...this.task, taskstate: 'Done' };
            return this.taskService.taskDone(taskToComplete);
          })
        )
        .subscribe({
          next: (newTaskId: string) => {
            this.submitting = false;
            emitSubmitted();
            this.taskCompleted.emit(newTaskId);
            
            // Close dialog if opened in one
            if (this.dialogRef) {
              this.dialogRef.close({ taskCompleted: true, newTaskId });
            }
          },
          error: (err) => {
            this.submitting = false;
            this.submitError = 'Failed to submit form. Please try again.';
            console.error('Form submit error:', err);
          }
        });
    } else if (this.projectId) {
      // Project-linked form: just submit response
      this.responseService
        .saveProjectResponse(this.organizationId, this.projectId, this.template.id, req)
        .subscribe({
          next: () => {
            this.submitting = false;
            emitSubmitted();
            
            // Close dialog if opened in one
            if (this.dialogRef) {
              this.dialogRef.close({ submitted: true });
            }
          },
          error: (err) => {
            this.submitting = false;
            this.submitError = 'Failed to submit form. Please try again.';
            console.error('Form submit error:', err);
          }
        });
    }
  }

  /**
   * Show confirmation modal before resetting form to draft
   */
  showResetToDraftConfirmation(): void {
    this.showResetConfirmModal = true;
    this.resetError = undefined;
  }

  /**
   * Close reset confirmation modal
   */
  closeResetConfirmModal(): void {
    this.showResetConfirmModal = false;
    this.resetError = undefined;
  }

  /**
   * Reset submitted form back to draft state
   * Only available to vendor/admin users viewing submitted forms
   */
  onResetToDraft(): void {
    if (!this.isVendorUser || !this.existingResponse || this.existingResponse.status !== 'submitted') {
      console.error('Reset to draft not allowed');
      return;
    }

    if (!this.organizationId || !this.template?.id) {
      console.error('Missing required IDs for reset operation');
      return;
    }

    this.resetting = true;
    this.resetError = undefined;

    this.responseService
      .resetToDraft(
        this.organizationId,
        this.template.id,
        this.taskId,
        this.projectId
      )
      .subscribe({
        next: (response) => {
          this.resetting = false;
          this.showResetConfirmModal = false;
          
          // Update local state
          if (this.existingResponse) {
            this.existingResponse.status = 'draft';
          }
          
          // Re-enable form controls
          this.changeDetectorRef.markForCheck();
          
          // Log analytics event
          //console.log('[FormRenderer] Form reset to draft - Analytics event:', {
          //   action: 'form_reset_to_draft_triggered',
          //   templateId: this.template.id,
          //   taskId: this.taskId,
          //   projectId: this.projectId,
          //   userId: SessionHelper.getUserSub(),
          //   timestamp: new Date().toISOString()
          // });
          
          // Emit event for parent components
          this.draftSaved.emit(response);
        },
        error: (err) => {
          this.resetting = false;
          this.resetError = 'Unable to reopen form. Please try again or contact support.';
          console.error('Reset to draft failed:', err);
          this.changeDetectorRef.markForCheck();
        }
      });
  }
}
